import {
  ActionButton,
  Buttons,
  CancelButton,
  DateTime,
  DeleteButton,
  DeleteConfirmation,
  Drawer,
  Field,
  Form,
  FormMessage,
} from '~/components';
import { useApi, useConfirmation, useToast } from '~/contexts';
import { Formik } from 'formik';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import React, { useRef } from 'react';
import { dateFormats, emptyStringToNull, getFileBytes, mergeValues } from '~/utils';
import * as Yup from 'yup';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import _ from 'lodash';
import { colors, weights } from '~/styles';
import styled from 'styled-components';

const Item = styled.div`
  flex: 1;
  padding-top: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid ${colors.grey10};

  &:last-child {
    border-bottom: none;
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: ${weights.medium};
  text-align: left;
  padding-top: 2px;
  padding-bottom: 2px;
`;

const Content = styled.div`
  font-size: 1rem;
  font-weight: ${weights.light};
  text-align: justify;
  padding-top: 2px;
  padding-bottom: 2px;
  white-space: pre-wrap;

  ul {
    margin-top: -0.5rem;
    padding-left: 1.5rem;
    list-style-type: disc;
    line-height: 1rem;
  }

  li {
    margin-bottom: 0.25rem;
    line-height: 1.5rem;
  }
`;

const Image = styled.img`
  display: block;
  margin: 2rem auto;
  border-radius: 6px;
  box-shadow: 0px 3px 45px 0px ${colors.grey10};
`;

function ProductUpdateDrawer({ entry, onSaved, onClose, onDeleted }) {
  const title = `${entry?.id ? 'Edit' : 'New'} Entry`;
  useDocumentTitle(title);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting, saved }, form] = useForm();
  const api = useApi();
  const toast = useToast();
  const confirmation = useConfirmation();

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  const initialValues = mergeValues(
    {
      date: moment().format('YYYY-MM-DD'),
      title: '',
      content: '',
      audience: '',
      isPromoted: true,
      isPublished: true,
      image: null,
      imageUrl: '',
    },
    entry,
  );

  function preserveBlankLines(text) {
    return text.replace(/(?<=\n\n)(?![*-])\n/gi, '&nbsp;\n ');
  }

  return (
    <Drawer
      isOpen
      title={title}
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}
      width="70rem">
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit(formRef.current.status.action);

            const body = emptyStringToNull({
              ..._.omit(values, 'image', 'imageUrl'),
            });

            const { data } = entry?.id
              ? await api.admin.productUpdates.update(entry.id, body)
              : await api.admin.productUpdates.create(body);

            if (values.image || values.imageUrl !== initialValues.imageUrl) {
              let file = null;
              if (values.image) {
                const bytes = await getFileBytes(values.image);
                const type = values.image.type;
                file = { bytes, type };
              }

              await api.admin.productUpdates.setImage(entry?.id ?? data.id, file);
            }

            await onSaved(data);
            form.save(formRef.current.status.action);

            closeDrawer();
            form.done();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        async function handleDelete() {
          const confirm = await confirmation.prompt((resolve) => (
            <DeleteConfirmation resolve={resolve}>Are you sure that you want to delete this entry?</DeleteConfirmation>
          ));
          if (!confirm) return;
          try {
            await api.admin.productUpdates.delete(entry.id);
            await onDeleted();
            closeDrawer();
          } catch ({ message }) {
            toast.error(message);
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              date: Yup.date().label('Date').nullable().required(),
              title: Yup.string().label('Title').max(255).required(),
              content: Yup.string().label('Content').max(5000).required(),
              audience: Yup.string().label('Audience').oneOf(['members', 'admins']).required(),
              isPromoted: Yup.bool().label('Promote this entry'),
              isPublished: Yup.bool().label('Publish this entry'),
              image: Yup.mixed().label('Image'),
            })}>
            {(formik) => {
              const submit = async (action) => {
                formik.setStatus({ action });
                formik.submitForm();
              };

              return (
                <Form>
                  <Form.Section title="Details">
                    <Form.Control>
                      <Field.DayPicker name="date" placeholder="Date" clearable={false} />
                    </Form.Control>
                    <Form.Control>
                      <Field.SingleSelect name="audience" placeholder="Audience">
                        <option value="members">All Members</option>
                        <option value="admins">Workspace Admins</option>
                      </Field.SingleSelect>
                    </Form.Control>
                    <Form.Control>
                      <Field.Checkbox name="isPromoted" label="Promote this entry" />
                    </Form.Control>
                    <Form.Control>
                      <Field.Checkbox name="isPublished" label="Publish this entry" />
                    </Form.Control>
                    <Form.Control>
                      <Field.Text name="title" placeholder="Title" maxLength={255} />
                    </Form.Control>
                    <Form.Control>
                      <Field.TextArea name="content" placeholder="Content" maxLength={5000} />
                    </Form.Control>
                    <Form.Control>
                      <Field.ImageFileInput name="image" urlFieldName="imageUrl" label="Image" />
                    </Form.Control>
                  </Form.Section>

                  <Form.Section title="Preview">
                    <Item>
                      <DateTime
                        value={formik.values.date}
                        format={dateFormats.longDate}
                        style={{ color: colors.grey40, fontSize: '0.825rem' }}
                      />
                      <Title>{formik.values.title}</Title>
                      <Content>
                        <ReactMarkdown>{preserveBlankLines(formik.values.content)}</ReactMarkdown>
                      </Content>
                      {formik.values.imageUrl && <Image src={formik.values.imageUrl} alt={formik.values.title} />}
                    </Item>
                  </Form.Section>

                  {status && <FormMessage.Error>{message}</FormMessage.Error>}

                  <Drawer.Actions>
                    {entry.id && <DeleteButton onClick={handleDelete}>Delete</DeleteButton>}

                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                      <ActionButton
                        isLoading={isSubmitting === 'close'}
                        ok={saved === 'close'}
                        type="submit"
                        onClick={() => submit('close')}>
                        Save &amp; Close
                      </ActionButton>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

export default ProductUpdateDrawer;
